import React, { useState, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Lock, CheckCircle, XCircle, Shield, UserPlus } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { getFooterText } from '@/config/version';

interface ValidationRule {
  id: string;
  label: string;
  test: (password: string) => boolean;
}

const validationRules: ValidationRule[] = [
  {
    id: 'length',
    label: 'At least 8 characters long',
    test: (password: string) => password.length >= 8,
  },
  {
    id: 'uppercase',
    label: 'Contains at least one uppercase letter',
    test: (password: string) => /[A-Z]/.test(password),
  },
  {
    id: 'lowercase',
    label: 'Contains at least one lowercase letter',
    test: (password: string) => /[a-z]/.test(password),
  },
  {
    id: 'number',
    label: 'Contains at least one number',
    test: (password: string) => /[0-9]/.test(password),
  },
  {
    id: 'special',
    label: 'Contains at least one special character',
    test: (password: string) => /[^A-Za-z0-9]/.test(password),
  },
];

const LeftSidebar = React.memo(() => (
  <div className="hidden lg:flex lg:w-[480px] xl:w-[540px] flex-shrink-0 relative bg-[#22201E] flex-col justify-between p-10 overflow-hidden">
    {/* Subtle dot pattern */}
    <div className="absolute inset-0 opacity-[0.04]">
      <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern id="invite-dots" width="20" height="20" patternUnits="userSpaceOnUse">
            <circle cx="1" cy="1" r="0.5" fill="white" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#invite-dots)" />
      </svg>
    </div>
    {/* Soft radial glow */}
    <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-white/[0.02] rounded-full blur-3xl" />

    {/* Top - Logo */}
    <div className="relative z-10">
      <img
        src="/nimbus_logo_white.svg"
        alt="Nimbus Healthcare"
        className="h-9"
      />
    </div>

    {/* Center - Headline */}
    <div className="relative z-10 space-y-6">
      <div>
        <h2 className="text-[42px] font-normal text-[#FAF9F6] leading-[1.15] tracking-tight font-fraunces">
          Welcome to
          <br />
          Your New
          <br />
          <span className="text-[#C37657]">Nimbus Providers.</span>
        </h2>
        <p className="text-[14px] text-white/50 mt-4 leading-relaxed max-w-[340px]">
          Set up your account to start managing patients, streamlining workflows, and elevating your healthcare experience.
        </p>
      </div>

      {/* Stats row */}
      <div className="flex items-center gap-6 pt-2">
        <div>
          <p className="text-[24px] font-bold text-[#FAF9F6]">HIPAA</p>
          <p className="text-[11px] text-white/40 mt-0.5">Fully compliant</p>
        </div>
        <div className="w-px h-10 bg-white/10" />
        <div>
          <p className="text-[24px] font-bold text-[#FAF9F6]">E-Rx</p>
          <p className="text-[11px] text-white/40 mt-0.5">E-Prescriptions</p>
        </div>
        <div className="w-px h-10 bg-white/10" />
        <div>
          <p className="text-[24px] font-bold text-[#FAF9F6]">24/7</p>
          <p className="text-[11px] text-white/40 mt-0.5">Patient access</p>
        </div>
      </div>

      {/* Feature list */}
      <div className="space-y-3 pt-2">
        {['Patient management & scheduling', 'Secure payments & invoicing', 'E-prescriptions & clinical workflows'].map((feature) => (
          <div key={feature} className="flex items-center gap-2.5">
            <div className="w-1 h-1 rounded-full bg-white/30 flex-shrink-0" />
            <p className="text-[12px] text-white/50">{feature}</p>
          </div>
        ))}
      </div>
    </div>

    {/* Bottom - Copyright */}
    <div className="relative z-10">
      <p className="text-[11px] text-white/25">
        &copy; {new Date().getFullYear()} Nimbus Healthcare. All rights reserved.
      </p>
    </div>
  </div>
));

const Invite = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isInvalidInvitation, setIsInvalidInvitation] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { acceptInvitation, verifyInvite } = useAuth();
  const { toast } = useToast();
  const token = searchParams.get('token');
  const role = searchParams.get('role');
  const clinicId = searchParams.get('clinic_id');

  // Real-time password validation
  const passwordValidation = useMemo(() => {
    if (!password) return validationRules.map(rule => ({ ...rule, isValid: false }));

    return validationRules.map(rule => ({
      ...rule,
      isValid: rule.test(password),
    }));
  }, [password]);

  const isPasswordValid = useMemo(() => {
    return passwordValidation.every(rule => rule.isValid);
  }, [passwordValidation]);

  const passwordsMatch = useMemo(() => {
    return password && confirmPassword && password === confirmPassword;
  }, [password, confirmPassword]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast({
        title: 'Passwords do not match',
        description: 'Please make sure your passwords match',
        variant: 'destructive',
      });
      return;
    }

    if (!isPasswordValid) {
      toast({
        title: 'Password requirements not met',
        description: 'Please ensure your password meets all requirements',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);

    try {
      await acceptInvitation(token, password, role, clinicId);
      toast({
        title: 'Password reset successfully!',
        description: 'You can now sign in with your new password.',
        variant: 'default',
      });
      navigate('/login');
    } catch (error) {
      console.error('Password reset failed:', error);
      toast({
        title: 'Failed to reset password. Please try again.',
        description: error.message || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const checkInvitation = async () => {
    if (token) {
      try {
        await verifyInvite(token);
      } catch (error) {
        console.error('Invitation check failed:', error);
        setIsInvalidInvitation(true);
      }
    }
  };

  useEffect(() => {
    if (token) {
      checkInvitation();
    }
  }, [token]);

  // If no token is provided, show error
  if (!token || isInvalidInvitation) {
    return (
      <div className="min-h-screen flex">
        <LeftSidebar />
        <div className="w-full lg:flex-1 flex flex-col bg-[#FAFAF8]" style={{ minHeight: '100vh' }}>
          <div className="flex items-center justify-center px-4 py-8 lg:p-8 flex-1">
            <motion.div
              className="w-full max-w-md"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              {/* Logo */}
              <div className="flex flex-col items-center mb-8">
                <img
                  src="/nimbus_logo.svg"
                  alt="Nimbus Healthcare"
                  className="h-10"
                />
                <div className="mt-3 flex items-center">
                  <div className="h-px w-8 bg-slate-300" />
                  <span className="mx-3 text-xs font-semibold text-brand-teal uppercase tracking-widest">
                    Clinic & Practitioner
                  </span>
                  <div className="h-px w-8 bg-slate-300" />
                </div>
              </div>

              <motion.div
                className="bg-white rounded-2xl shadow-xl shadow-gray-200/50 p-8 border border-gray-100 text-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-4">
                  <XCircle className="h-8 w-8 text-red-500" />
                </div>
                <h1 className="text-xl font-semibold text-gray-900 mb-1">
                  Invalid Invitation Link
                </h1>
                <p className="text-sm text-gray-500 mb-6">
                  This invitation link is invalid or has expired.
                </p>
                <Button
                  onClick={() => navigate('/login')}
                  className="w-full h-12 rounded-xl bg-brand-teal hover:bg-brand-teal/90 text-white text-base font-semibold shadow-lg shadow-brand-teal/25 hover:shadow-xl hover:shadow-brand-teal/30 transition-all duration-300"
                >
                  Return to Login
                </Button>
              </motion.div>
            </motion.div>
          </div>
          <div className="p-6 text-center text-xs text-gray-400 mt-auto">
            <p>{getFooterText()}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Branded Visual (same as Login) */}
      <LeftSidebar />

      {/* Right Side - Invite Form */}
      <div className="w-full lg:flex-1 flex flex-col bg-[#FAFAF8]" style={{ minHeight: '100vh' }}>
        {/* Form Container */}
        <div className="flex items-start justify-center px-4 py-12 lg:p-8 lg:pt-16 flex-1 overflow-y-auto">
          <motion.div
            className="w-full max-w-md"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Logo */}
            <div className="flex flex-col items-center mb-8">
              <img
                src="/nimbus_logo.svg"
                alt="Nimbus Healthcare"
                className="h-10"
              />
              <div className="mt-3 flex items-center">
                <div className="h-px w-8 bg-slate-300" />
                <span className="mx-3 text-xs font-semibold text-brand-teal uppercase tracking-widest">
                  Clinic & Practitioner
                </span>
                <div className="h-px w-8 bg-slate-300" />
              </div>
            </div>

            {/* Form Card */}
            <motion.div
              className="bg-white rounded-2xl shadow-xl shadow-gray-200/50 p-8 border border-gray-100"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              {/* Header inside card */}
              <div className="mb-6">
                <h1 className="text-xl font-semibold text-gray-900 mb-1">
                  Set up your account
                </h1>
                <p className="text-sm text-gray-500">
                  Create a password to get started with your account
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your new password"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      required
                      className="h-12 rounded-xl border-gray-200 focus:border-brand-teal focus:ring-brand-teal/20 text-base placeholder:text-gray-400 transition-all pr-12"
                      minLength={8}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>

                  {/* Password validation messages */}
                  {password && (
                    <motion.div
                      className="space-y-2 mt-3 p-3 rounded-xl border border-gray-100 bg-slate-50"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.2 }}
                    >
                      <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">Password requirements</p>
                      <div className="space-y-1.5">
                        {passwordValidation.map((rule, index) => (
                          <motion.div
                            key={rule.id}
                            className={`flex items-center space-x-1.5 text-xs transition-colors duration-200 ${
                              rule.isValid ? 'text-green-600' : 'text-gray-400'
                            }`}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.2, delay: index * 0.05 }}
                          >
                            {rule.isValid ? (
                              <CheckCircle className="h-3.5 w-3.5 flex-shrink-0" />
                            ) : (
                              <XCircle className="h-3.5 w-3.5 flex-shrink-0" />
                            )}
                            <span>{rule.label}</span>
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm New Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Confirm your new password"
                      value={confirmPassword}
                      onChange={e => setConfirmPassword(e.target.value)}
                      required
                      className="h-12 rounded-xl border-gray-200 focus:border-brand-teal focus:ring-brand-teal/20 text-base placeholder:text-gray-400 transition-all pr-12"
                      minLength={8}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {confirmPassword && (
                  <motion.div
                    className="flex items-center space-x-2 text-sm"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.2 }}
                  >
                    {passwordsMatch ? (
                      <>
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <span className="text-green-600 font-medium">Passwords match</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="h-4 w-4 text-red-500" />
                        <span className="text-red-500 font-medium">Passwords do not match</span>
                      </>
                    )}
                  </motion.div>
                )}

                <Button
                  type="submit"
                  className="w-full h-12 rounded-xl bg-brand-teal hover:bg-brand-teal/90 text-white text-base font-semibold shadow-lg shadow-brand-teal/25 hover:shadow-xl hover:shadow-brand-teal/30 transition-all duration-300"
                  disabled={isLoading || !passwordsMatch || !isPasswordValid}
                >
                  {isLoading ? (
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Setting up account...</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <Lock className="h-4 w-4" />
                      <span>Create Account</span>
                    </div>
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-sm text-gray-500">
                  Already have an account?{' '}
                  <button
                    type="button"
                    className="text-sm text-brand-teal hover:text-brand-teal/80 font-medium transition-colors"
                    onClick={() => navigate('/login')}
                  >
                    Sign in instead
                  </button>
                </p>
              </div>
            </motion.div>

            {/* Security Note */}
            <motion.div
              className="mt-8 flex items-center justify-center gap-2 text-gray-400 text-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              <Shield className="w-4 h-4" />
              <span>Secured with enterprise-grade encryption</span>
            </motion.div>
          </motion.div>
        </div>

        {/* Footer */}
        <div className="p-6 text-center text-xs text-gray-400 mt-auto">
          <p>{getFooterText()}</p>
        </div>
      </div>
    </div>
  );
};

export default Invite;
